import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import ModalHeader from '@components/modals/ModalHeader/ModalHeader';
import Button from '@components/uikit/Button/Button';
import Input from '@components/uikit/Input/Input';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useLocationsGetOneQuery } from '@services/useLocationsGetOneQuery';
import { useLocationsSaveMutation } from '@services/useLocationsSaveMutation';

import { useModalActions } from '@store/useModalStore';

import {
  EDIT_LOCATION_FIELDS,
  editLocationDefaultValue,
  schemaEditLocation,
} from './constants/schemaEditLocation';
import style from './style.module.scss';

const EditLocationNameModal = ({ locationId, locationName, parentId }) => {
  const { t } = useTranslation();

  const { setModalClose } = useModalActions();

  const { data: assetTreeData } = useAssetTreeQuery();
  const groupedLocationsById = assetTreeData.locations.reduce((acc, item) => {
    if (!item.parentId) {
      return acc;
    }

    const parentId = item.parentId;

    if (!acc[parentId]) {
      acc[parentId] = [];
    }

    acc[parentId].push(item);
    return acc;
  }, {});

  const { data: dataOneLocation } = useLocationsGetOneQuery({ locationId });
  const { mutate: locationsSaveMutate, isLoading: locationsSaveIsLoading } = useLocationsSaveMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm({
    mode: 'onChange',
    resolver: schemaEditLocation(locationName, groupedLocationsById[parentId]),
    defaultValues: editLocationDefaultValue(locationName, dataOneLocation),
  });

  useEffect(() => {
    if (dataOneLocation) {
      reset({
        [EDIT_LOCATION_FIELDS.ASSET_NAME]: dataOneLocation.parent.name,
      });
    }
  }, [dataOneLocation]);

  const submit = handleSubmit((formData) => {
    locationsSaveMutate(
      {
        id: locationId,
        name: formData.new_name,
        parent_id: dataOneLocation.parent.id,
        parentId: dataOneLocation.parent.id,
      },
      {
        onSuccess: () => {
          showToast({ variant: 'success', message: t('The location has been successfully changed.') });

          handleClose();
        },
      },
    );
  });

  const handleClose = () => {
    setModalClose();
    reset();
  };

  return (
    <div className={style.modalEditLocation}>
      <ModalHeader />
      <div className={style.modalBody}>
        <div className={style.modalBodyTitle}>
          {t('Edit Location Name')} <br />"{locationName}"
        </div>
        <div className={style.modalBodyForm}>
          <Input
            label={t('Asset Tree')}
            name={EDIT_LOCATION_FIELDS.ASSET_NAME}
            control={control}
            disabled
            required
          />
          <Input label={t('New Name')} name={EDIT_LOCATION_FIELDS.NEW_NAME} control={control} required />
        </div>
      </div>
      <div className={style.modalFooter}>
        <Button
          type="primary"
          text={t('Save')}
          disabled={locationsSaveIsLoading || !isDirty}
          onClick={submit}
        />
        <Button type="secondary" text={t('Cancel')} disabled={locationsSaveIsLoading} onClick={handleClose} />
      </div>
    </div>
  );
};

export default EditLocationNameModal;
